import React from "react";
import { Link } from "react-router-dom";
import { Clock, Users, ArrowRight, Handshake } from "lucide-react";
import { useSite } from "../lib/SiteContext";
import { mediaUrl } from "../lib/api";

const PartnerChartersSection = () => {
  const { charters } = useSite();
  const partners = (charters || []).filter(
    (c) => c.active !== false && c.type === "partner"
  );
  if (partners.length === 0) return null;

  return (
    <section
      id="partners"
      className="section-padding bg-muted/40"
      data-testid="partner-charters-section"
    >
      <div className="container-x">
        <div className="grid lg:grid-cols-12 gap-8 items-end mb-14 md:mb-20">
          <div className="lg:col-span-7">
            <div className="overline mb-4 inline-flex items-center gap-2">
              <Handshake size={12} /> Partner Charters
            </div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight text-primary text-balance">
              Additional boats from trusted local skippers.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pl-8">
            <p className="text-muted-foreground leading-relaxed">
              When our own boat is booked or you&rsquo;re looking for something different, we work with a handful of
              hand-picked Maltese charter partners. Request a date and we&rsquo;ll confirm availability with them within
              24 hours — no charge until we do.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {partners.map((c) => (
            <article
              key={c.id}
              className="group bg-white border border-border hover:border-primary/30 transition-all duration-500 hover:-translate-y-1 hover:shadow-xl flex flex-col"
              data-testid={`partner-charter-${c.slug}`}
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={mediaUrl(c.image_url)}
                  alt={c.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute top-4 left-4 px-3 py-1 bg-secondary text-white text-[10px] tracking-[0.25em] uppercase font-semibold">
                  Partner
                </div>
              </div>

              <div className="p-7 flex-1 flex flex-col">
                <div className="flex items-center gap-4 text-xs uppercase tracking-[0.2em] text-secondary mb-3">
                  <span className="flex items-center gap-1.5">
                    <Clock size={12} /> {c.start_time}–{c.end_time}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Users size={12} /> Up to {c.max_guests}
                  </span>
                </div>
                <h3 className="font-serif text-2xl md:text-3xl text-primary leading-tight mb-1">
                  {c.title}
                </h3>
                {c.partner_name && (
                  <div className="text-xs text-muted-foreground tracking-wider mb-3">
                    Operated by {c.partner_name}
                  </div>
                )}
                <p className="text-sm text-muted-foreground mb-5 leading-relaxed">{c.subtitle}</p>

                <div className="hairline mb-5 mt-auto" />
                <div className="flex items-end justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">From</div>
                    <div className="font-serif text-3xl text-primary">
                      €{Number(c.price_eur).toLocaleString()}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      deposit €{Number(c.deposit_eur).toLocaleString()} on approval
                    </div>
                  </div>
                  <Link
                    to={`/book?charter=${c.id}`}
                    className="inline-flex items-center gap-2 px-5 py-2.5 border border-primary text-primary text-sm hover:bg-primary hover:text-white transition-colors"
                    style={{ borderRadius: "2px" }}
                    data-testid={`request-partner-${c.slug}`}
                  >
                    Request date <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PartnerChartersSection;
